import React, { useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { MultiSelect as MS } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';

import SubText from '../FormComponenets/SubText';
import { RenderContent } from '../FormComponenets/RenderContent';
import LabelName from 'components/UI/LabelName/LabelName';
import SelectableSubFieldWrapper from '../FormComponenets/SelectableSubFieldWrapper';
import useGetListAPI from 'modules/intakeforms/hooks/useGetListAPI';
import { generateFullName } from 'utils/generateFullNameUtils';
import { findArrayDifferences } from 'utils/utils';
import { uuidv4 } from 'common/uuidGenerator';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';

export default function RelationField(props) {
  const { index, handleSettingsClick, isFormBuilder } = props;
  const [highlight, setHighlight] = useState(-1);
  const {
    watch,
    setValue,
    control,
    getValues,
    formState: { errors },
    resetField,
  } = useFormContext();
  const item = watch(`form_data.${index}`);
  const { contactList } = useGetListAPI();

  const { append, fields, remove } = useFieldArray({
    name: `form_data.${index}.temp_contacts_mapping`,
    control,
  });

  const deleteSubField = (subIndex) => {
    setValue(
      `form_data.${index}.subFields`,
      item?.subFields?.filter((f, i) => i !== subIndex)
    );
  };

  const relationName = `form_data.${index}.relations`;
  const relationFields = item?.subFields;

  const addNewRelation = () => {
    const _id = uuidv4();
    append({ is_new: true, contact_id: _id, new_id: _id });
    setValue(`contacts.${_id}`, { is_new: true, title: '', new_id: _id });
    resetField(relationName);
  };

  //keeping all the data in contacts?.{contact_id}.{contact data object}
  const addExistingRelation = (contacts, callBack) => {
    const old_contacts = getValues(relationName) || [];
    const { added, removed } = findArrayDifferences(old_contacts, contacts);
    if (added?.[0]) {
      const contact = contactList?.find((v) => v?.contact_id === added?.[0]);
      append(contact);
      if (!getValues(`contacts.${added?.[0]}`))
        setValue(`contacts.${added?.[0]}`, {
          ...contact,
          custom_field: contact?.custom_field?.reduce((acc, v) => ({ ...acc, [v?.custom_field_id]: v?.value }), {}),
          role: contact?.role || 'Client',
        });
    }
    if (removed?.[0]) {
      //remove from contacts object if only one item left
      const form_data = watch('form_data');
      const relationsCount = form_data
        ?.filter((v) => v?.type === 'relation')
        ?.reduce((acc, v) => {
          return [...acc, ...(v?.temp_contacts_mapping || [])];
        }, [])
        ?.filter((v) => v?.contact_id === removed?.[0])?.length;
      relationsCount === 1 && setValue(`contacts.${removed?.[0]}`, false);

      const index = fields?.findIndex((v) => v?.contact_id === removed?.[0]);
      index > -1 && remove(index);
    }
    callBack(contacts);
  };

  const removeRelation = (index, subItem) => {
    //remove from contacts object if only one item left
    const form_data = watch('form_data');
    const relationsCount = form_data
      ?.filter((v) => v?.type === 'relation')
      ?.reduce((acc, v) => {
        return [...acc, ...(v?.temp_contacts_mapping || [])];
      }, [])
      ?.filter((v) => v?.contact_id === subItem?.contact_id)?.length;
    relationsCount === 1 && setValue(`contacts.${subItem?.contact_id}`, false);

    remove(index);
    const selectedContacts = getValues(relationName) || [];
    setValue(
      relationName,
      selectedContacts?.filter((v) => v !== subItem?.contact_id)
    );
  };

  const validate = (value) => {
    if (item?.required) {
      if (fields?.length > 0 || value?.length > 0) {
        return true;
      } else return 'Please select at least one contact or add a new contact';
    } else return true;
  };

  return (
    <div className={isFormBuilder ? 'px-3 mt-2' : ''} style={{ background: '#ffffffbf' }}>
      <div className="d-flex align-items-center justify-content-between px-0">
        <div className="d-flex flex-column">
          <LabelName className="pe-2 mb-1">
            {item.label || item?.name} {item?.required && <span className="danger-text"> *</span>}
          </LabelName>
          <Controller
            name={relationName}
            control={control}
            rules={isFormBuilder ? {} : { validate: validate }}
            render={({ field, fieldState }) => (
              <MS
                id={field.name}
                options={contactList || []}
                itemTemplate={(v) => <>{generateFullName(v)}</>}
                optionLabel="first_name"
                optionValue="contact_id"
                display="chip"
                onChange={(e) => {
                  addExistingRelation(e.value, field.onChange);
                }}
                value={field.value || []}
                placeholder="Select options"
                className={classNames({ 'p-invalid': fieldState.invalid, 'new-task-popup input-shadow': true })}
                style={{ pointerEvents: isFormBuilder ? 'none' : 'auto', width: 350 }}
                inputRef={isFormBuilder ? null : field.ref}
              />
            )}
          />
          <SubText {...{ subtext: item?.subtext, handleSettingsClick, item, isFormBuilder }} />
          <RequiredValidation field={relationName} errors={errors} />
        </div>
        <Button
          label="Add"
          onClick={addNewRelation}
          disabled={isFormBuilder}
          icon="pi pi-plus"
          className="p-button p-component p-button-secondary outline"
        />
      </div>
      {isFormBuilder ? (
        <Droppable droppableId={`subList-${item._id}`} type="SUB">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} style={{ borderRadius: 8, background: '#fafafad4', marginTop: 12 }}>
              {relationFields.map((subItem, subIndex) => (
                <Draggable key={index + 'sub' + subIndex} draggableId={index + 'sub' + subIndex} index={subIndex}>
                  {(provided) => (
                    <div
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      style={{
                        borderRadius: '4px',
                        ...provided.draggableProps.style,
                      }}
                      onClick={() => setHighlight(subIndex)}
                    >
                      <SelectableSubFieldWrapper
                        item={subItem}
                        removeItem={() => deleteSubField(subIndex)}
                        highlighted={highlight === subIndex}
                        handleSettingsClick={() => handleSettingsClick(item, subItem)}
                      >
                        {RenderContent({
                          condition: subItem.type,
                          props: {
                            index,
                            subIndex,
                            item: subItem,
                            handleSettingsClick: () => handleSettingsClick(item, subItem),
                            isFormBuilder,
                          },
                        })}
                      </SelectableSubFieldWrapper>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ) : (
        <>
          {fields.map((subItem, subIndex) => (
            <div style={{ borderRadius: 8, background: '#fafafa', position: 'relative' }} key={subItem?.id} className="pb-2 mt-2 pt-1">
              <i
                className="pi pi-trash cursor-pointer danger-text"
                style={{ position: 'absolute', top: 10, right: 10 }}
                onClick={() => removeRelation(subIndex, subItem)}
              />
              {relationFields?.map((field, i) => {
                let relationFieldName;
                if (field?.res_type === 'custom-field') {
                  relationFieldName = `contacts.${subItem?.contact_id}.custom_field.${field?.custom_field_id}`;
                } else {
                  relationFieldName = `contacts.${subItem?.contact_id}.${field?.relationFieldName}`;
                }
                return (
                  <div key={field?.id || i} className="p-2 px-3">
                    {RenderContent({
                      condition: field.type,
                      props: {
                        index,
                        subIndex,
                        item: field,
                        handleSettingsClick: () => handleSettingsClick(item, field),
                        isFormBuilder,
                        isRelation: true,
                        relationFieldName,
                      },
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
